import { relations } from 'drizzle-orm'
import { index, integer, pgTable, text, timestamp, unique, uuid } from 'drizzle-orm/pg-core'
import type { NotificationDeliveryStatus } from '../../constants/training-enums'
import { users } from '../schema'
import { tenants } from './tenants'

// Notification Templates
export const notificationTemplates = pgTable(
  'notification_templates',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    notificationType: text('notification_type').notNull(),
    subject: text('subject').notNull(),
    body: text('body').notNull(),
    availableVariables: text('available_variables').array().notNull(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  (table) => ({
    tenantTypeUnique: unique().on(table.tenantId, table.notificationType),
    tenantIdIdx: index('notification_templates_tenant_id_idx').on(table.tenantId),
  }),
)

// Notification Logs（送信キュー兼ログ: 登録時は queued、配信ジョブが accepted / failed に更新する）
export const notificationLogs = pgTable(
  'notification_logs',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    notificationType: text('notification_type').notNull(),
    channel: text('channel').notNull(),
    recipientId: uuid('recipient_id')
      .notNull()
      .references(() => users.id),
    /** 登録時点の宛先。配信までに利用者のメールアドレスが変わっても登録時の宛先に送る */
    recipientEmail: text('recipient_email').notNull(),
    subject: text('subject').notNull(),
    body: text('body').notNull(),
    status: text('status').$type<NotificationDeliveryStatus>().notNull().default('queued'),
    attempts: integer('attempts').notNull().default(0),
    /** 直近に配信を試みた時刻。送信数制限の枠はこの時刻で数える */
    lastAttemptAt: timestamp('last_attempt_at'),
    /** この時刻以降に配信を試みる。配信中はリース期限として先送りされる */
    nextAttemptAt: timestamp('next_attempt_at').notNull().defaultNow(),
    errorMessage: text('error_message'),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    createdAt: timestamp('created_at').notNull().defaultNow(),
    /** 送信先が受け付けた時刻（accepted のときのみ） */
    sentAt: timestamp('sent_at'),
  },
  (table) => ({
    recipientIdIdx: index('notification_logs_recipient_id_idx').on(table.recipientId),
    tenantIdIdx: index('notification_logs_tenant_id_idx').on(table.tenantId),
    deliveryQueueIdx: index('notification_logs_delivery_queue_idx').on(
      table.tenantId,
      table.status,
      table.nextAttemptAt,
    ),
    lastAttemptAtIdx: index('notification_logs_last_attempt_at_idx').on(
      table.tenantId,
      table.lastAttemptAt,
    ),
  }),
)

// Relations
export const notificationLogsRelations = relations(notificationLogs, ({ one }) => ({
  recipient: one(users, {
    fields: [notificationLogs.recipientId],
    references: [users.id],
  }),
}))

// Type exports
export type NotificationTemplate = typeof notificationTemplates.$inferSelect
export type NewNotificationTemplate = typeof notificationTemplates.$inferInsert

export type NotificationLog = typeof notificationLogs.$inferSelect
export type NewNotificationLog = typeof notificationLogs.$inferInsert
